import React, { useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Helmet from 'react-helmet';

import { IApplicationState } from '@store';
import { HOCLoader, InvalidMessage } from '@components';
import {
  IDownloadResultsPackageAPIResponse,
  IDownloadResultsPackageForUI,
} from '@models';

import {
  useParsedSummaryData,
  countInactiveSamples,
} from './helpers/results-package-helper';
import PackageInfoContainer from './components/DownloadResultsPackage/PackageInfoContainer';
import DownloadResultsTableContainer from './components/DownloadResultsPackage/DownloadResultsTableContainer';
import ExpiredLinkBanner from './components/DownloadResultsPackage/ExpiredLinkBanner';
import './DownloadResultsPackage.scss';

export interface DownloadResultsStateProps {
  accountTimeZone: string;
}

const DownloadResultsPackage: React.FC<DownloadResultsStateProps> = ({
  accountTimeZone,
}: DownloadResultsStateProps) => {
  const { t } = useTranslation();
  const { packageId } = useParams<{ packageId: string }>();
  const [downloadResultsPkg, setDownloadResultsPkg] =
    useState<IDownloadResultsPackageAPIResponse | null>(null);
  const [uiReportSummaries, setUIReportSummaries] = useState<
    IDownloadResultsPackageForUI[]
  >([]);
  const [inactiveSamples, setInactiveSamples] = useState<number>(0);

  const projectId = useSelector(
    (state: IApplicationState) => state.projectPicker.activeProjectId
  );

  const timezone = useSelector(
    (state: IApplicationState) => state.projectPicker.timeZone
  );
  const { requestStatus, summaryDetail, summaryList } =
    useParsedSummaryData(packageId);

  React.useEffect(() => {
    if (requestStatus === 2 && summaryDetail && summaryList.length > 0) {
      setDownloadResultsPkg(
        summaryDetail as IDownloadResultsPackageAPIResponse
      );
      setUIReportSummaries(summaryList);
      setInactiveSamples(
        countInactiveSamples(
          (summaryDetail as IDownloadResultsPackageAPIResponse).reportSummaries
        )
      );
    }
  }, [requestStatus]);

  return (
    <div className='download-results-package'>
      <Helmet>
        <title>{t('download_tab_title')}</title>
      </Helmet>
      <Row className='title-container'>
        <Col className='d-flex align-items-end pl-0'>
          <h5>{t('downloadResultsPkg_title')}</h5>
        </Col>
      </Row>
      <HOCLoader
        isEmpty={downloadResultsPkg === null}
        status={requestStatus}
        customError={
          <InvalidMessage
            title={t('downloadResultsPkg_invalidResultsTitle')}
            description={t('downloadResultsPkg_invalidResultsDescription')}
          />
        }
      >
        {downloadResultsPkg && projectId && timezone ? (
          <>
            {downloadResultsPkg!.pkgInfo.state === 'ReportNA' ? (
              <ExpiredLinkBanner
                packageId={downloadResultsPkg!.pkgInfo.id}
                packageName={downloadResultsPkg!.pkgInfo.name}
              ></ExpiredLinkBanner>
            ) : null}
            <Container fluid className='download-info-container'>
              <PackageInfoContainer
                downloadPackage={downloadResultsPkg}
                inactiveSamples={inactiveSamples}
                packageId={packageId}
                timezone={timezone}
                uiSummaryListLength={uiReportSummaries.length}
                projectId={projectId}
              ></PackageInfoContainer>
              <hr className='my-1' />
              <DownloadResultsTableContainer
                uiSummaryList={uiReportSummaries}
              ></DownloadResultsTableContainer>
            </Container>
          </>
        ) : null}
      </HOCLoader>
    </div>
  );
};

export default DownloadResultsPackage;
